import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Pencil, Check, X, Trash2, Compass, Heart, BookOpen, Wind, RotateCcw, ShoppingBag } from 'lucide-react';
import { getWellnessProfile, updateWellnessProfile, wellnessOptions, wellnessRecommendations, deleteQuizResult } from '../api';
import { useToast } from '../hooks/use-toast';

const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs font-jost transition-all ${active ? 'bg-gradient-to-r from-[#d4af37] to-[#f0d582] text-[#0a0708]' : 'border border-white/15 text-[#f5ecd9]/70 hover:border-[#d4af37]/40'}`}>{children}</button>
);

const Section = ({ title, children }) => (
  <div className="card-glass rounded-2xl p-5 sm:p-6">
    <h3 className="font-cinzel text-lg text-[#f0d582] mb-3">{title}</h3>
    {children}
  </div>
);

export const WellnessProfile = () => {
  const { toast } = useToast();
  const [wp, setWp] = useState(null);
  const [opts, setOpts] = useState(null);
  const [recs, setRecs] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    const [p, o] = await Promise.all([getWellnessProfile(), wellnessOptions()]);
    setWp(p.wellness_profile); setOpts(o);
    try { setRecs(await wellnessRecommendations()); } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(wp))); setEditing(true); };
  const toggle = (field, val) => setDraft((d) => { const arr = d[field] || []; return { ...d, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] }; });
  const toggleBeautyMulti = (field, val) => setDraft((d) => { const b = { ...(d.beauty || {}) }; const arr = b[field] || []; b[field] = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]; return { ...d, beauty: b }; });
  const setBeautySingle = (field, val) => setDraft((d) => ({ ...d, beauty: { ...(d.beauty || {}), [field]: (d.beauty || {})[field] === val ? '' : val } }));

  const save = async () => {
    try {
      const r = await updateWellnessProfile({ spiritual_interests: draft.spiritual_interests || [], goals: draft.goals || [], beauty: draft.beauty || {} });
      setWp(r.wellness_profile); setEditing(false);
      setRecs(await wellnessRecommendations());
      toast({ title: 'Profile updated' });
    } catch (e) { toast({ title: 'Could not save', description: e?.response?.data?.detail }); }
  };

  const removeQuiz = async (key) => {
    if (!window.confirm('Remove this discovery result from your profile?')) return;
    try {
      const r = await deleteQuizResult(key); setWp(r.wellness_profile);
      setRecs(await wellnessRecommendations());
      toast({ title: 'Result removed' });
    } catch (e) { toast({ title: 'Could not remove', description: e?.response?.data?.detail }); }
  };

  if (!wp || !opts) return <p className="font-cormorant text-lg text-[#f5ecd9]/50">Loading your profile…</p>;

  const quizResults = wp.quiz_results || {};
  const beauty = wp.beauty || {};
  const hasBeauty = Object.values(beauty).some((v) => (Array.isArray(v) ? v.length : v));

  return (
    <div className="space-y-6" data-testid="wellness-profile">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-cinzel text-2xl text-gold-gradient">My Wellness Profile</h2>
          <p className="font-jost text-xs text-[#f5ecd9]/50 mt-1">Private to you. Shapes your personalized Prestige experience.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/discover" data-testid="take-discovery-btn" className="rounded-full px-4 py-2 text-xs uppercase tracking-widest font-jost border border-[#d4af37]/40 text-[#f0d582] hover:bg-[#d4af37]/10"><Compass className="h-3.5 w-3.5 inline mr-1" /> Take a Discovery</Link>
          {!editing && <button data-testid="edit-profile-btn" onClick={startEdit} className="rounded-full px-4 py-2 text-xs uppercase tracking-widest font-jost bg-gradient-to-r from-[#d4af37] to-[#f0d582] text-[#0a0708]"><Pencil className="h-3.5 w-3.5 inline mr-1" /> Edit</button>}
        </div>
      </div>

      {editing ? (
        <div className="space-y-6" data-testid="wellness-editor">
          <Section title="Spiritual Interests">
            <div className="flex flex-wrap gap-2">{opts.interests.map((i) => <Chip key={i} testid={`interest-${i}`} active={(draft.spiritual_interests || []).includes(i)} onClick={() => toggle('spiritual_interests', i)}>{i}</Chip>)}</div>
          </Section>
          <Section title="Current Goals"><p className="font-jost text-xs text-[#f5ecd9]/40 mb-2">What are you focusing on right now?</p>
            <div className="flex flex-wrap gap-2">{opts.interests.map((i) => <Chip key={i} testid={`goal-${i}`} active={(draft.goals || []).includes(i)} onClick={() => toggle('goals', i)}>{i}</Chip>)}</div>
          </Section>
          <Section title="Beauty & Hair (optional)">
            {Object.entries(opts.beauty_options).map(([field, values]) => {
              const single = field === 'hair_texture';
              const cur = (draft.beauty || {})[field];
              return (
                <div key={field} className="mb-4">
                  <p className="font-jost text-[11px] uppercase tracking-widest text-[#f5ecd9]/50 mb-2">{field.replace(/_/g, ' ')}</p>
                  <div className="flex flex-wrap gap-2">{values.map((v) => <Chip key={v} active={single ? cur === v : (cur || []).includes(v)} onClick={() => single ? setBeautySingle(field, v) : toggleBeautyMulti(field, v)}>{v}</Chip>)}</div>
                </div>
              );
            })}
          </Section>
          <div className="flex gap-3">
            <button data-testid="save-profile-btn" onClick={save} className="btn-gold rounded-full px-6 py-2.5 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]"><Check className="h-4 w-4 inline mr-1" /> Save</button>
            <button onClick={() => setEditing(false)} className="rounded-full px-5 py-2.5 text-sm font-jost text-[#f5ecd9]/60 border border-white/15"><X className="h-4 w-4 inline mr-1" /> Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <Section title="Spiritual Interests">
              {(wp.spiritual_interests || []).length ? <div className="flex flex-wrap gap-2">{wp.spiritual_interests.map((i) => <span key={i} className="px-3 py-1 rounded-full text-xs font-jost bg-white/5 text-[#f5ecd9]/80 border border-white/10">{i}</span>)}</div> : <p className="font-cormorant text-[#f5ecd9]/40">None selected yet — tap Edit to add.</p>}
            </Section>
            <Section title="Current Goals">
              {(wp.goals || []).length ? <div className="flex flex-wrap gap-2">{wp.goals.map((i) => <span key={i} className="px-3 py-1 rounded-full text-xs font-jost bg-white/5 text-[#f5ecd9]/80 border border-white/10">{i}</span>)}</div> : <p className="font-cormorant text-[#f5ecd9]/40">None set yet.</p>}
            </Section>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {(opts.quizzes || []).map((qz) => {
              const k = qz.key;
              return (
                <Section key={k} title={qz.title}>
                  {quizResults[k] ? (
                    <div data-testid={`saved-quiz-${k}`}>
                      <p className="font-cinzel text-lg text-gold-gradient">{quizResults[k].primary_label}</p>
                      {quizResults[k].secondary_label && <p className="font-cormorant text-[#f5ecd9]/65">with {quizResults[k].secondary_label} influences</p>}
                      <div className="flex gap-3 mt-3">
                        <Link to="/discover" className="text-[11px] uppercase tracking-widest font-jost text-[#f0d582]"><RotateCcw className="h-3 w-3 inline mr-1" />Retake</Link>
                        <button data-testid={`remove-quiz-${k}`} onClick={() => removeQuiz(k)} className="text-[11px] uppercase tracking-widest font-jost text-[#ff4da6]"><Trash2 className="h-3 w-3 inline mr-1" />Remove</button>
                      </div>
                    </div>
                  ) : (
                    <div><p className="font-cormorant text-[#f5ecd9]/40 mb-2">Not discovered yet.</p><Link to="/discover" className="text-[11px] uppercase tracking-widest font-jost text-[#f0d582]">Take {qz.title} →</Link></div>
                  )}
                </Section>
              );
            })}
          </div>

          {hasBeauty && (
            <Section title="Beauty & Hair Preferences">
              <div className="flex flex-wrap gap-2">
                {Object.entries(beauty).map(([f, v]) => (Array.isArray(v) ? v : [v]).filter(Boolean).map((val) => <span key={f + val} className="px-3 py-1 rounded-full text-xs font-jost bg-white/5 text-[#f5ecd9]/80 border border-white/10">{val}</span>))}
              </div>
            </Section>
          )}

          {recs && recs.based_on && recs.based_on.length > 0 && (
            <Section title="Recommended For You">
              <p className="font-jost text-xs text-[#f5ecd9]/45 mb-4">Based on: {recs.based_on.join(', ')}</p>
              {recs.products.length > 0 && (
                <div className="mb-5">
                  <p className="font-jost text-[11px] uppercase tracking-widest text-[#f0d582]/70 mb-2"><ShoppingBag className="h-3.5 w-3.5 inline mr-1" />Products</p>
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="rec-products">
                    {recs.products.map((p) => (
                      <Link key={p.id} to={`/product/${p.id}`} className="flex gap-3 items-center rounded-xl p-3 border border-white/8 hover:border-[#d4af37]/40 transition-all">
                        {p.image && <img src={p.image} alt={p.name} className="h-14 w-14 rounded-lg object-cover" />}
                        <div className="min-w-0"><p className="font-cinzel text-sm text-[#f5ecd9] truncate">{p.name}</p><p className="font-jost text-[11px] text-[#f5ecd9]/50 leading-snug">{p.reason}</p></div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                {recs.programs.length > 0 && <div><p className="font-jost text-[11px] uppercase tracking-widest text-[#f0d582]/70 mb-2">Programs</p>{recs.programs.map((p) => <Link key={p.id} to="/programs" className="block mb-2"><span className="font-cinzel text-sm text-[#f5ecd9]">{p.title}</span><span className="block font-jost text-[11px] text-[#f5ecd9]/50">{p.reason}</span></Link>)}</div>}
                {recs.services.length > 0 && <div><p className="font-jost text-[11px] uppercase tracking-widest text-[#f0d582]/70 mb-2">Services</p>{recs.services.map((s) => <Link key={s.id} to="/book" className="block mb-2"><span className="font-cinzel text-sm text-[#f5ecd9]">{s.name}</span><span className="block font-jost text-[11px] text-[#f5ecd9]/50">{s.reason}</span></Link>)}</div>}
              </div>
              {(recs.practices.length > 0 || recs.prompts.length > 0) && (
                <div className="grid sm:grid-cols-2 gap-5 mt-5">
                  {recs.practices.length > 0 && <div><p className="font-jost text-[11px] uppercase tracking-widest text-[#f0d582]/70 mb-2"><Wind className="h-3.5 w-3.5 inline mr-1" />Practices</p>{recs.practices.map((p, i) => <p key={i} className="font-cormorant text-[#f5ecd9]/70 mb-1.5">• {p}</p>)}</div>}
                  {recs.prompts.length > 0 && <div><p className="font-jost text-[11px] uppercase tracking-widest text-[#f0d582]/70 mb-2"><BookOpen className="h-3.5 w-3.5 inline mr-1" />Journal Prompts</p>{recs.prompts.map((p, i) => <p key={i} className="font-cormorant text-[#f5ecd9]/70 mb-1.5">• {p}</p>)}</div>}
                </div>
              )}
              {recs.next_quizzes && recs.next_quizzes.length > 0 && (
                <div className="mt-5 pt-4 border-t border-white/8"><p className="font-jost text-[11px] uppercase tracking-widest text-[#f0d582]/70 mb-2"><Sparkles className="h-3.5 w-3.5 inline mr-1" />Discover Next</p>{recs.next_quizzes.map((q) => <Link key={q.key} to="/discover" className="inline-block mr-3 font-cinzel text-sm text-[#f5ecd9] underline decoration-[#d4af37]/40">{q.title}</Link>)}</div>
              )}
            </Section>
          )}

          {(!recs || !recs.based_on || recs.based_on.length === 0) && (
            <Section title="Recommended For You">
              <p className="font-cormorant text-[#f5ecd9]/50">Add a few interests or take a Discovery, and personalized recommendations will appear here.</p>
              <Link to="/discover" className="inline-block mt-3 text-[11px] uppercase tracking-widest font-jost text-[#f0d582]">Start a Discovery →</Link>
            </Section>
          )}
        </>
      )}
    </div>
  );
};

export default WellnessProfile;
